import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, ShieldCheck, Award, CheckCircle } from 'lucide-react';
import usePageMeta from '../hooks/usePageMeta';

const MILD_BOTTLE = 'https://customer-assets-m6fa6gv7.emergentagent.net/job_044e7215-b684-4e8a-bc75-b8b1576b06e1/artifacts/1vxmk0tt_ChatGPT%20Image%20Aug%2023%2C%202026%2C%2012_48_37%20AM.png';
const PROTEIN_BOTTLE = 'https://customer-assets-m6fa6gv7.emergentagent.net/job_044e7215-b684-4e8a-bc75-b8b1576b06e1/artifacts/3dw6oeub_Strong%20Pint.webp';

const INGREDIENTS = [
  { icon: Leaf, title: 'German Malts', desc: 'Imported Pilsner & Munich malts for a clean, biscuit-forward base.' },
  { icon: Award, title: 'Organic Hops', desc: 'German Magnum hops from certified organic farms.' },
  { icon: ShieldCheck, title: 'Spring Water', desc: 'Soft natural spring water for a crisp mouthfeel.' },
  { icon: CheckCircle, title: 'Clean Label', desc: 'No added sugar. No glycerine. No artificial preservatives.' },
];

const OurBeer = () => {
  usePageMeta({
    title: 'Our Beer | Low Calorie Mild Lager & Protein Strong Lager',
    description: 'Two clean-brewed expressions: Low Calorie Mild Lager <5% ABV (Reinheitsgebot) and Protein + Collagen Strong Lager <8% ABV. Recipe developed with VLB Berlin.',
    path: '/our-beer',
  });

  return (
  <main className="nb-page nb-page-beer" data-testid="page-our-beer">
    <section className="nb-page-hero">
      <div className="nb-page-hero-inner">
        <p className="nb-eyebrow">OUR BEER</p>
        <h1 className="nb-display-md">TWO EXPRESSIONS. ONE STANDARD.</h1>
        <p className="nb-page-lede">
          Both variants are brewed to a recipe developed with VLB Berlin using German malts, organic Magnum hops
          and soft spring water. Nothing added — no sugar, no glycerine, no artificial preservatives.
        </p>
      </div>
    </section>

    {/* MILD LAGER */}
    <section className="nb-beer-block" id="mild" data-testid="beer-mild">
      <div className="nb-beer-block-inner">
        <div className="nb-beer-visual">
          <img src={MILD_BOTTLE} alt="No-Belly Mild Lager 330ml bottle" />
        </div>
        <div className="nb-beer-body">
          <span className="nb-tag">AVAILABLE</span>
          <h2 className="nb-display-sm">LOW CALORIE<br />MILD LAGER</h2>
          <p className="nb-abv">&lt;5% ABV · MILD LAGER</p>
          <p className="nb-copy">
            Crisp, clean and easy-drinking. Brewed to the German purity standard (Reinheitsgebot) —
            water, malt, hops and yeast. Nothing else.
          </p>

          <div className="nb-spec-grid">
            <div className="nb-spec"><span>ABV</span><strong>&lt;5%</strong></div>
            <div className="nb-spec"><span>Style</span><strong>Mild Lager</strong></div>
            <div className="nb-spec"><span>Formats</span><strong>330ml Pint · 650ml Bottle · 500ml Can</strong></div>
            <div className="nb-spec"><span>Standard</span><strong>Reinheitsgebot Compliant</strong></div>
          </div>

          <div className="nb-callout">
            <p><strong>Ingredients:</strong> water, German Pilsner &amp; Munich malts, organic Magnum hops, yeast.</p>
          </div>

          <Link to="/find-us" className="nb-cta-primary" data-testid="beer-mild-find">Find Mild Lager<ArrowRight size={16} /></Link>
        </div>
      </div>
    </section>

    {/* PROTEIN + COLLAGEN */}
    <section className="nb-beer-block nb-beer-block-red" id="protein" data-testid="beer-protein">
      <div className="nb-beer-block-inner nb-beer-block-inner-reverse">
        <div className="nb-beer-visual">
          <img src={PROTEIN_BOTTLE} alt="No-Belly Protein + Collagen Strong Lager 650ml bottle" />
        </div>
        <div className="nb-beer-body">
          <span className="nb-tag nb-tag-red">AVAILABLE</span>
          <h2 className="nb-display-sm">PROTEIN + COLLAGEN<br />STRONG LAGER</h2>
          <p className="nb-abv nb-abv-red">&lt;8% ABV · STRONG LAGER</p>
          <p className="nb-copy">
            A strong lager brewed with the same 100% German base — imported Pilsner &amp; Munich malts,
            organic Magnum hops and soft spring water — then infused with hydrolysed protein and vegan
            collagen. Bold flavour, without the belly.
          </p>

          <div className="nb-spec-grid">
            <div className="nb-spec"><span>ABV</span><strong>&lt;8%</strong></div>
            <div className="nb-spec"><span>Style</span><strong>Strong Lager</strong></div>
            <div className="nb-spec"><span>Formats</span><strong>650ml Bottle · 500ml Can</strong></div>
            <div className="nb-spec"><span>Base</span><strong>100% German Ingredients</strong></div>
            <div className="nb-spec"><span>Adjuncts</span><strong>Hydrolysed Protein · Vegan Collagen</strong></div>
          </div>

          <div className="nb-callout nb-callout-red">
            <p><strong>Ingredients:</strong> water, German Pilsner &amp; Munich malts, organic Magnum hops, yeast, hydrolysed protein, vegan collagen.</p>
          </div>

          <Link to="/find-us" className="nb-cta-ghost nb-cta-ghost-red" data-testid="beer-protein-find">Find Strong Lager<ArrowRight size={16} /></Link>
        </div>
      </div>
    </section>

    {/* NON-ALC COMING SOON */}
    <section className="nb-beer-soon" data-testid="beer-nonalc">
      <div className="nb-beer-soon-inner">
        <span className="nb-tag">COMING SOON</span>
        <h2 className="nb-display-sm">NON-ALCOHOLIC LAGER</h2>
        <p className="nb-abv">0.0% ABV · NON-ALCOHOLIC</p>
        <p className="nb-copy">
          The same clean-brewed profile — with zero alcohol. Launching soon in 330ml Pints.
        </p>
      </div>
    </section>

    {/* INGREDIENT PILLARS */}
    <section className="nb-pillars">
      <header className="nb-section-head">
        <p className="nb-eyebrow">CLEAN BREWED</p>
        <h2 className="nb-display-md">PREMIUM GERMAN INGREDIENTS. NOTHING UNNECESSARY.</h2>
      </header>
      <div className="nb-pillars-grid">
        {INGREDIENTS.map((p, i) => (
          <div key={i} className="nb-pillar" data-testid={`beer-pillar-${i}`}>
            <p.icon size={28} className="nb-pillar-icon" />
            <h3 className="nb-pillar-title">{p.title}</h3>
            <p className="nb-copy">{p.desc}</p>
          </div>
        ))}
      </div>
    </section>

    <section className="nb-page-cta" data-testid="beer-cta-strip">
      <h2 className="nb-display-sm">READY TO POUR?</h2>
      <p className="nb-copy">Find No-Belly at retailers and 50+ HORECA partners across Gurgaon, Faridabad, Panchkula &amp; Goa.</p>
      <Link to="/find-us" className="nb-cta-primary nb-cta-lg" data-testid="beer-cta-find-us">Find No-Belly Near You<ArrowRight size={18} /></Link>
    </section>
  </main>
  );
};

export default OurBeer;
